import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { Checkbox } from 'primereact/checkbox';
import { MultiSelect } from 'primereact/multiselect';
import axiosInstance from '../../config/axios.config';

/**
 * Employee Register by Admin Dialog Component
 * Professional dialog for company admins to register new employees
 *
 * Features:
 * - Email OTP verification
 * - Manual or auto-generated password
 * - Professional UI with PrimeReact components
 * - Form validation
 * - Success/error handling
 */
const EmployeeRegisterByAdminDialog = ({ visible, onHide, onSuccess }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        mobile_number: '',
        handled_lenders: [],
    });

    // Lenders this employee handles (multi-select). Options fetched when the
    // dialog opens; each option = { label: brand/legal name, value: lender id }.
    const [lenderOptions, setLenderOptions] = useState([]);
    const [loadingLenders, setLoadingLenders] = useState(false);

    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [verifyingOtp, setVerifyingOtp] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [skipEmailVerification, setSkipEmailVerification] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Password generation states
    const [passwordMode, setPasswordMode] = useState('manual'); // 'manual' or 'generated'
    const [showPassword, setShowPassword] = useState(false);

    // Seat usage { current, cap, allowed } — cap null = unlimited.
    const [seats, setSeats] = useState(null);

    const toast = useRef(null);

    // Load current seat usage each time the dialog opens.
    useEffect(() => {
        if (!visible) return;
        let active = true;
        axiosInstance.get('/company/seats')
            .then((res) => { if (active && res?.data?.success) setSeats(res.data.data); })
            .catch((e) => console.error('Failed to load seats:', e?.message || 'Unknown error'));
        return () => { active = false; };
    }, [visible]);

    // Load lenders for the multi-select when the dialog opens (once).
    // /lenders/dropdown returns { success, data: [{ value, label, code, type }] }.
    useEffect(() => {
        if (!visible || lenderOptions.length > 0) return;
        let active = true;
        setLoadingLenders(true);
        axiosInstance.get('/company/lenders-dropdown')
            .then((res) => {
                if (!active) return;
                const list = res?.data?.data || [];
                setLenderOptions(list.map((l) => ({ label: l.label, value: l.value })));
            })
            .catch((e) => console.error('Failed to load lenders:', e?.message || 'Unknown error'))
            .finally(() => { if (active) setLoadingLenders(false); });
        return () => { active = false; };
    }, [visible, lenderOptions.length]);

    // Generate secure random password
    const generateSecurePassword = () => {
        const length = 12;
        const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lowercase = "abcdefghijklmnopqrstuvwxyz";
        const numbers = "0123456789";
        const special = "!@#$%^&*";
        const allChars = uppercase + lowercase + numbers + special;

        let password = "";

        // Ensure at least one of each type
        password += uppercase[Math.floor(Math.random() * uppercase.length)];
        password += lowercase[Math.floor(Math.random() * lowercase.length)];
        password += numbers[Math.floor(Math.random() * numbers.length)];
        password += special[Math.floor(Math.random() * special.length)];

        // Fill rest randomly
        for (let i = password.length; i < length; i++) {
            password += allChars[Math.floor(Math.random() * allChars.length)];
        }

        // Shuffle password
        return password.split('').sort(() => Math.random() - 0.5).join('');
    };

    // Handle generate password
    const handleGeneratePassword = () => {
        const newPassword = generateSecurePassword();
        setFormData({
            ...formData,
            password: newPassword,
            password_confirmation: newPassword
        });
        setPasswordMode('generated');
        setShowPassword(true);

        toast.current?.show({
            severity: 'success',
            summary: 'Password Generated',
            detail: 'Secure password generated successfully',
            life: 3000
        });
    };

    // Handle manual password mode
    const handleManualPasswordMode = () => {
        setPasswordMode('manual');
        setFormData({
            ...formData,
            password: '',
            password_confirmation: ''
        });
        setShowPassword(false);
    };

    // Copy password to clipboard
    const handleCopyPassword = async () => {
        try {
            await navigator.clipboard.writeText(formData.password);
            toast.current?.show({
                severity: 'success',
                summary: 'Copied',
                detail: 'Password copied to clipboard',
                life: 2000
            });
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to copy password',
                life: 3000
            });
        }
    };

    // Send OTP - Uses existing endpoint
    const handleSendOtp = async () => {
        if (!formData.email) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Email Required',
                detail: 'Please enter employee email address first',
                life: 3000
            });
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast.current?.show({
                severity: 'error',
                summary: 'Invalid Email',
                detail: 'Please enter a valid email address',
                life: 3000
            });
            return;
        }

        setSendingOtp(true);
        try {
            // Use existing OTP endpoint
            const response = await axiosInstance.post('/auth/send-otp', {
                email: formData.email
            });

            if (response.data.success) {
                setOtpSent(true);
                toast.current?.show({
                    severity: 'success',
                    summary: 'OTP Sent',
                    detail: `Verification code sent to ${formData.email}`,
                    life: 5000
                });
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to send OTP',
                life: 5000
            });
        } finally {
            setSendingOtp(false);
        }
    };

    // Verify OTP (inline check)
    const handleVerifyOtp = () => {
        if (otp.length === 6) {
            setEmailVerified(true);
            toast.current?.show({
                severity: 'success',
                summary: 'Email Verified',
                detail: 'Email verification successful. Complete the form to register.',
                life: 3000
            });
        } else {
            toast.current?.show({
                severity: 'error',
                summary: 'Invalid OTP',
                detail: 'Please enter a valid 6-digit OTP',
                life: 3000
            });
        }
    };

    // Resend OTP
    const handleResendOtp = async () => {
        setOtp('');
        await handleSendOtp();
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!formData.first_name.trim()) {
            errors.first_name = 'First name is required';
        }

        if (!formData.last_name.trim()) {
            errors.last_name = 'Last name is required';
        }

        if (!skipEmailVerification && !emailVerified) {
            errors.email = 'Please verify employee email with OTP or enable "Skip verification"';
        }

        if (!formData.mobile_number || formData.mobile_number.trim() === '') {
            errors.mobile_number = 'Mobile number is required';
        } else if (!/^[0-9]{10}$/.test(formData.mobile_number)) {
            errors.mobile_number = 'Please enter a valid 10-digit mobile number';
        }

        if (!formData.password || formData.password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        }

        if (formData.password !== formData.password_confirmation) {
            errors.password_confirmation = 'Password and confirmation do not match';
        }

        setFieldErrors(errors);

        if (Object.keys(errors).length > 0) {
            // Show first error as toast
            const firstError = Object.values(errors)[0];
            toast.current?.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: firstError,
                life: 3000
            });
            return false;
        }

        return true;
    };

    // Register employee
    const handleRegister = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        try {
            // Prepare payload
            const payload = { ...formData };
            // Only include OTP if not skipping verification
            if (!skipEmailVerification && otp) {
                payload.otp = otp;
            }

            // Call new endpoint
            const response = await axiosInstance.post('/company/register-employee-by-admin', payload);

            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Employee Registered',
                    detail: 'Employee registered successfully. Welcome email sent.',
                    life: 5000
                });

                // Reset form
                resetForm();

                // Close dialog and trigger success callback
                setTimeout(() => {
                    onHide();
                    if (onSuccess) onSuccess();
                }, 1500);
            }
        } catch (error) {
            // Extract specific validation errors if available
            let errorMessage = error.response?.data?.message || 'Failed to register employee';

            // Seat limit (409) — show a clear warning + refresh the seat banner.
            if (error.response?.status === 409 && error.response?.data?.code === 'seat_limit_reached') {
                if (error.response.data.seats) setSeats(error.response.data.seats);
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Seat limit reached',
                    detail: error.response.data.message || 'Upgrade your plan to add more users.',
                    life: 6000,
                });
                setSubmitting(false);
                return;
            }

            // Check for validation errors object (Laravel returns errors as object with field names)
            if (error.response?.data?.errors) {
                const backendErrors = error.response.data.errors;
                // Map backend errors to fieldErrors for inline display
                const newFieldErrors = {};
                Object.keys(backendErrors).forEach(field => {
                    if (backendErrors[field]?.length > 0) {
                        newFieldErrors[field] = backendErrors[field][0];
                    }
                });
                setFieldErrors(prev => ({ ...prev, ...newFieldErrors }));

                // Use first error as toast message
                const firstErrorField = Object.keys(backendErrors)[0];
                if (firstErrorField && backendErrors[firstErrorField]?.length > 0) {
                    errorMessage = backendErrors[firstErrorField][0];
                }
            }

            toast.current?.show({
                severity: 'error',
                summary: 'Registration Failed',
                detail: errorMessage,
                life: 5000
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            password_confirmation: '',
            mobile_number: '',
            handled_lenders: [],
        });
        setOtp('');
        setOtpSent(false);
        setEmailVerified(false);
        setPasswordMode('manual');
        setShowPassword(false);
        setSkipEmailVerification(false);
        setFieldErrors({});
    };

    // Handle dialog hide
    const handleHide = () => {
        resetForm();
        onHide();
    };

    // Dialog footer
    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={handleHide}
                className="p-button-text p-button-secondary"
                disabled={submitting}
            />
            <Button
                label="Register Employee"
                icon="pi pi-user-plus"
                onClick={handleRegister}
                loading={submitting}
                disabled={(!skipEmailVerification && !emailVerified) || submitting}
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-user-plus text-primary text-2xl"></i>
                        <span className="font-semibold">Register New Employee</span>
                    </div>
                }
                visible={visible}
                onHide={handleHide}
                footer={dialogFooter}
                style={{ width: '600px' }}
                modal
                dismissableMask={false}
                className="employee-register-dialog"
            >
                <form onSubmit={handleRegister}>
                    {/* Seat usage — shows "X of N users" and warns when full */}
                    {seats && (
                        <Message
                            severity={seats.allowed ? 'info' : 'warn'}
                            className="w-full mb-3"
                            content={
                                <span className="flex align-items-center gap-2 text-sm">
                                    <i className={`pi ${seats.allowed ? 'pi-users' : 'pi-exclamation-triangle'}`} />
                                    {seats.cap === null
                                        ? `${seats.current} users · Unlimited seats`
                                        : (seats.allowed
                                            ? `${seats.current} of ${seats.cap} users used`
                                            : `Seat limit reached — ${seats.current} of ${seats.cap} users. Upgrade your plan to add more.`)}
                                </span>
                            }
                        />
                    )}

                    {/* Personal Information Section */}
                    <div className="mb-4">
                        <h6 className="text-900 mb-3">
                            <i className="pi pi-user mr-2"></i>
                            Personal Information
                        </h6>

                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="first_name" className="font-medium">
                                        First Name <span className="text-red-500">*</span>
                                    </label>
                                    <InputText
                                        id="first_name"
                                        value={formData.first_name}
                                        onChange={(e) => {
                                            setFormData({...formData, first_name: e.target.value});
                                            if (fieldErrors.first_name) setFieldErrors(prev => ({ ...prev, first_name: null }));
                                        }}
                                        className={`w-full ${fieldErrors.first_name ? 'p-invalid' : ''}`}
                                        placeholder="e.g., John"
                                    />
                                    {fieldErrors.first_name && (
                                        <small className="p-error block mt-1">{fieldErrors.first_name}</small>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="last_name" className="font-medium">
                                        Last Name <span className="text-red-500">*</span>
                                    </label>
                                    <InputText
                                        id="last_name"
                                        value={formData.last_name}
                                        onChange={(e) => {
                                            setFormData({...formData, last_name: e.target.value});
                                            if (fieldErrors.last_name) setFieldErrors(prev => ({ ...prev, last_name: null }));
                                        }}
                                        className={`w-full ${fieldErrors.last_name ? 'p-invalid' : ''}`}
                                        placeholder="e.g., Doe"
                                    />
                                    {fieldErrors.last_name && (
                                        <small className="p-error block mt-1">{fieldErrors.last_name}</small>
                                    )}
                                </div>
                            </div>

                            {/* Lenders Handled — optional multi-select */}
                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="handled_lenders" className="font-medium">
                                        Lender Manager
                                    </label>
                                    <MultiSelect
                                        id="handled_lenders"
                                        value={formData.handled_lenders}
                                        options={lenderOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setFormData({ ...formData, handled_lenders: e.value })}
                                        placeholder={loadingLenders ? 'Loading lenders…' : 'Select lenders this employee handles'}
                                        display="chip"
                                        filter
                                        showClear
                                        className="w-full mt-2"
                                        emptyMessage="No lenders found"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <Divider />

                    {/* Contact Information Section */}
                    <div className="mb-4">
                        <h6 className="text-900 mb-3">
                            <i className="pi pi-envelope mr-2"></i>
                            Contact & Verification
                        </h6>

                        {/* Skip Email Verification Checkbox */}
                        <div className="field mb-3">
                            <div className="flex align-items-center gap-2">
                                <Checkbox
                                    inputId="skip_otp"
                                    checked={skipEmailVerification}
                                    onChange={(e) => {
                                        setSkipEmailVerification(e.checked);
                                        if (e.checked) {
                                            // Clear OTP states when skipping
                                            setOtpSent(false);
                                            setEmailVerified(false);
                                            setOtp('');
                                        }
                                    }}
                                />
                                <label htmlFor="skip_otp" className="ml-2">
                                    Skip email OTP verification
                                </label>
                            </div>
                            <small className="text-600 block mt-1">
                                Employee will receive welcome email with password reset link
                            </small>
                        </div>

                        {/* Email with OTP Verification */}
                        {!skipEmailVerification ? (
                        <>
                            <div className="field">
                                <label htmlFor="email" className="font-medium">
                                    Email Address <span className="text-red-500">*</span>
                                </label>
                                <div className="p-inputgroup mt-2">
                                    <InputText
                                        id="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        placeholder="employee@example.com"
                                        className="w-full"
                                        disabled={emailVerified}
                                    />
                                    {!emailVerified && (
                                        <Button
                                            type="button"
                                            label={otpSent ? "Resend" : "Send OTP"}
                                            icon={sendingOtp ? "pi pi-spin pi-spinner" : "pi pi-envelope"}
                                            onClick={otpSent ? handleResendOtp : handleSendOtp}
                                            disabled={sendingOtp || !formData.email}
                                            className="p-button-success"
                                        />
                                    )}
                                    {emailVerified && (
                                        <Button
                                            type="button"
                                            icon="pi pi-check-circle"
                                            aria-label="Email Verified"
                                            className="p-button-success"
                                            disabled
                                            tooltip="Email Verified"
                                        />
                                    )}
                                </div>
                                {emailVerified && (
                                    <small className="text-green-600 mt-1 block">
                                        <i className="pi pi-check-circle mr-1"></i>
                                        Email verified successfully
                                    </small>
                                )}
                            </div>

                            {/* OTP Input */}
                            {otpSent && !emailVerified && (
                                <div className="field mt-3">
                                    <label htmlFor="otp" className="font-medium">
                                        Verification Code
                                    </label>
                                    <div className="p-inputgroup mt-2">
                                        <InputText
                                            id="otp"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="Enter 6-digit code"
                                            className="w-full"
                                            maxLength={6}
                                        />
                                        <Button
                                            type="button"
                                            label="Verify"
                                            icon="pi pi-check"
                                            onClick={handleVerifyOtp}
                                            disabled={otp.length !== 6}
                                            className="p-button-primary"
                                        />
                                    </div>
                                    <Message
                                        severity="info"
                                        text={`OTP sent to ${formData.email}. Ask employee to check their email.`}
                                        className="mt-2 w-full"
                                        style={{ fontSize: '0.875rem' }}
                                    />
                                </div>
                            )}
                        </>
                        ) : (
                        // Simple email input when skipping verification
                        <div className="field">
                            <label htmlFor="email" className="font-medium">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <InputText
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => {
                                    setFormData({...formData, email: e.target.value});
                                    if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: null }));
                                }}
                                placeholder="employee@example.com"
                                className={`w-full mt-2 ${fieldErrors.email ? 'p-invalid' : ''}`}
                            />
                            {fieldErrors.email && (
                                <small className="p-error block mt-1">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {fieldErrors.email}
                                </small>
                            )}
                        </div>
                        )}

                        {/* Mobile Number */}
                        <div className="field mt-3">
                            <label htmlFor="mobile_number" className="font-medium">
                                Mobile Number <span className="text-red-500">*</span>
                            </label>
                            <InputText
                                id="mobile_number"
                                value={formData.mobile_number}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    setFormData({...formData, mobile_number: value});
                                    // Clear mobile error when user starts typing
                                    if (fieldErrors.mobile_number) {
                                        setFieldErrors(prev => ({ ...prev, mobile_number: null }));
                                    }
                                }}
                                className={`w-full mt-2 ${fieldErrors.mobile_number ? 'p-invalid' : ''}`}
                                placeholder="e.g., 9876543210"
                                maxLength={10}
                                keyfilter="int"
                            />
                            {fieldErrors.mobile_number && (
                                <small className="p-error block mt-1">
                                    <i className="pi pi-exclamation-circle mr-1"></i>
                                    {fieldErrors.mobile_number}
                                </small>
                            )}
                        </div>
                    </div>

                    <Divider />

                    {/* Password Section */}
                    <div className="mb-4">
                        <h6 className="text-900 mb-3">
                            <i className="pi pi-lock mr-2"></i>
                            Password Setup
                        </h6>

                        {/* Password Mode Selector */}
                        <div className="field">
                            <div className="flex gap-2 mb-3">
                                <Button
                                    type="button"
                                    label="Set Manually"
                                    icon="pi pi-key"
                                    className={passwordMode === 'manual' ? 'p-button-sm' : 'p-button-outlined p-button-sm'}
                                    onClick={handleManualPasswordMode}
                                />
                                <Button
                                    type="button"
                                    label="Generate Password"
                                    icon="pi pi-refresh"
                                    className={passwordMode === 'generated' ? 'p-button-success p-button-sm' : 'p-button-outlined p-button-success p-button-sm'}
                                    onClick={handleGeneratePassword}
                                />
                            </div>

                            {/* Manual Password Input */}
                            {passwordMode === 'manual' && (
                                <div>
                                    <label htmlFor="password" className="font-medium">
                                        Password <span className="text-red-500">*</span>
                                    </label>
                                    <Password
                                        id="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        className="w-full mt-2"
                                        placeholder="Enter password (min 8 characters)"
                                        toggleMask
                                        inputClassName="w-full"
                                    />

                                    <label htmlFor="password_confirmation" className="font-medium mt-3 block">
                                        Confirm Password <span className="text-red-500">*</span>
                                    </label>
                                    <Password
                                        id="password_confirmation"
                                        value={formData.password_confirmation}
                                        onChange={(e) => setFormData({...formData, password_confirmation: e.target.value})}
                                        className="w-full mt-2"
                                        placeholder="Re-enter password"
                                        toggleMask
                                        feedback={false}
                                        inputClassName="w-full"
                                    />
                                </div>
                            )}

                            {/* Generated Password Display */}
                            {passwordMode === 'generated' && formData.password && (
                                <div className="p-3 bg-green-50 border-round border-1 border-green-200">
                                    <div className="flex align-items-center justify-content-between mb-2">
                                        <span className="font-semibold text-green-700">Generated Password:</span>
                                        <div className="flex gap-1">
                                            <Button
                                                type="button"
                                                icon={showPassword ? "pi pi-eye-slash" : "pi pi-eye"}
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                className="p-button-text p-button-sm p-button-success"
                                                onClick={() => setShowPassword(!showPassword)}
                                                tooltip={showPassword ? "Hide" : "Show"}
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                            <Button
                                                type="button"
                                                icon="pi pi-copy"
                                                aria-label="Copy password"
                                                className="p-button-text p-button-sm p-button-success"
                                                onClick={handleCopyPassword}
                                                tooltip="Copy"
                                                tooltipOptions={{ position: 'top' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="bg-white p-2 border-round">
                                        <code className="text-xl font-bold text-green-900">
                                            {showPassword ? formData.password : '••••••••••••'}
                                        </code>
                                    </div>
                                    <small className="text-600 mt-2 block">
                                        <i className="pi pi-info-circle mr-1"></i>
                                        This password will be sent to the employee via welcome email
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </Dialog>
        </>
    );
};

export default EmployeeRegisterByAdminDialog;
